import { useEffect } from 'react';
import store from "../../../../store";
import workspace from "../../components/canvas/core/workspace";
import sqftShapeManager from "../../components/canvas/store/sqftShapeManager";

/**
 * Applies the feature the user has just picked to the sqft shapes of the cart item being edited.
 *
 * Two different "features" are involved here, it is easy to mix them up:
 *
 * 1) cartState.cartItemEditFeature - the cart item the user pressed "Edit" on in the cart.
 *    Holds whatever is currently saved in the quote for this item (e.g. "Level 07"). Used here
 *    both to find which shapes belong to that cart item, and as the value to write into them.
 *
 * 2) selectionState.activeFeature - the feature currently applied to the VISUALIZER layout.
 *    It is NOT necessarily what is saved in the cart: for price-level features (e.g. "LEVEL 07")
 *    the layout keeps showing the underlying stone (e.g. "Alpine Mist"), so activeFeature stays
 *    on the material, not the price tier. Relying on it as the value to write is what caused
 *    QCDES-8812 (Level 7 reverting to Alpine Mist): the shapes already correctly held "Level 07",
 *    this hook ran on mount, matched them, and overwrote them with activeFeature ("Alpine Mist").
 *
 * sqftState.sqftFromFeature.cartFeature is used as the primary source instead of activeFeature.
 * When entering from the cart (featureInfo.js) it is set to: the feature the user is currently
 * viewing (selectionFeatureInfo) if set, otherwise cartItemEditFeature. selectionFeatureInfo wins
 * because the user may have picked a different material to view/apply that hasn't been confirmed
 * into the cart yet - cartItemEditFeature only updates once changeCartFeature is dispatched, so it
 * can lag behind what is actually on screen. activeFeature is kept only as a fallback for the rare
 * case sqftFromFeature was not set.
 */
const useUpdateEditedFeatures = () => {

  //modify backsplash, countertops & edges on edit from cart click
  useEffect (() => {
    const state = store.getState();
    const {cartItemEditFeature} = state.cartState;
    const {activeFeature} = state.selectionState;
    const {sqftFromFeature} = state.sqftState;

    const featureToApply = sqftFromFeature?.cartFeature || activeFeature;

    if (!cartItemEditFeature || !featureToApply || cartItemEditFeature.partId !== featureToApply.partId) {
      return;
    }

    // shape id(s) this cart item was drawn as - set on cartItem.sqftId/sqftIds when the item is
    // added from the SQFT calculator (see parseSqftData.js, "sqftId: featurePassed.id" -
    // featurePassed.id is a workspace shape id, same as the id sqftShapeManager.setShapeFeature
    // expects). More reliable than matching by featureId: it can't collide with another item
    // (unlike featureId, which can be identical for two different areas sharing the same
    // material), and it still resolves even if the shape's featureId has drifted out of sync
    // with the cart (broken/legacy quotes).
    const linkedShapeIds = [
      ...(Array.isArray(cartItemEditFeature.sqftIds) ? cartItemEditFeature.sqftIds : []),
      ...(cartItemEditFeature.sqftId !== undefined && cartItemEditFeature.sqftId !== null ? [cartItemEditFeature.sqftId] : []),
    ];
    const linkedShapes = linkedShapeIds
      .map(shapeId => workspace.getShapeById(shapeId))
      .filter(Boolean);

    if (linkedShapes.length) {
      for (const shape of linkedShapes) {
        const isParentFeature = shape.parentPartId === featureToApply.partId;
        if (isParentFeature && typeof shape.changeFeatureIdIfColorWasChanged === "function") {
          shape.changeFeatureIdIfColorWasChanged(featureToApply);
        } else if (shape.partId === featureToApply.partId) {
          sqftShapeManager.setShapeFeature(shape.id, featureToApply);
        }
      }
      sqftShapeManager.updateShapesWizardProperties();
      return;
    }

    // Fallback for cart items with no sqftId (not added from the calculator, e.g. via
    // getCartItemObject()) - locate shapes by feature id, same behaviour as before.
    const sqftFeaturesByPartId = workspace.getShapesByPartId(featureToApply.partId);
    if (sqftFeaturesByPartId && sqftFeaturesByPartId.length) {
      //include slab shapes
      const sqftFeaturesByFeatureId = workspace.getShapesByFeatureId(sqftFeaturesByPartId[0].featureId);
      for (const sqftFeature of sqftFeaturesByFeatureId) {
        // only shapes that still hold the edited cart item feature, nothing else is touched.
        if (sqftFeature.featureId === cartItemEditFeature?.cartFeature.dbFeature?.id) {
          sqftShapeManager.setShapeFeature(sqftFeature.id, featureToApply);
          sqftShapeManager.updateShapesWizardProperties();
        }
      }
    }
  }, []);
};

export default useUpdateEditedFeatures;
